import React, {Component, Fragment} from "react";
import {connect} from "react-redux";

import TemplateTab from "../tabs/TemplateTab";
import ConfigTab from "../tabs/ConfigTab";
import * as templateHelper from "../helper/template";
import ErrorBoundary from "../common/ErrorBoundary";
import UploadLogoModal from "../config-items/partials/UploadLogoModal";
import {showNotification} from "../../../store/slices/admin/notification";
import {saveDashboard} from "../../../store/slices/admin/dashboards";
import {IGridConfigItem, ITemplateConfig} from "../../../constants/interfaces";
import { fetchBigscreenSettings } from "../../../store/slices/admin/bigscreenSettings";

type Props = {
    // injected
    dispatch: Function
}

type State = {
    activeTab: string,
    templateConfig: ITemplateConfig,
    templateId: number | string,
    renameActive: boolean,
    modalOpen: boolean,
    logo: {
        url: string,
        name: string
    } | null
}

class CreateDashboard extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        const defaultTemplateId = templateHelper.getDefaultTemplateId();
        const defaultTemplate = templateHelper.getTemplateDefaultConfiguration(defaultTemplateId);
        this.state = {
            activeTab: "template",
            templateConfig: defaultTemplate,
            templateId: defaultTemplateId,
            renameActive: false,
            modalOpen: false,
            logo: null
        }

        this.toggleRenameField = this.toggleRenameField.bind(this);
        this.changeName = this.changeName.bind(this);
        this.selectTemplate = this.selectTemplate.bind(this);
        this.updateConfig = this.updateConfig.bind(this);
        this.toggleModal = this.toggleModal.bind(this);
        this.uploadLogo = this.uploadLogo.bind(this);
        this.saveDashboard = this.saveDashboard.bind(this);
        this.switchTabViaTemplateDoubleClick = this.switchTabViaTemplateDoubleClick.bind(this)
    }

    componentDidMount() {
        this.props.dispatch(fetchBigscreenSettings());
    	this.setName();
	}

	componentDidUpdate(prevProps: any, prevState: { templateId: any; }) {
    	if (prevState.templateId !== this.state.templateId) {
			this.setName();
		}
	}

	setName() {
	   const templateConfig = {...this.state.templateConfig};
	   if (!templateConfig.description) {
		   templateConfig.description = "Example dashboard";
		   this.setState({templateConfig});
	   }
   }

	switchTab(tab: string) {
        this.setState({activeTab: tab});
    }

    switchTabViaTemplateDoubleClick() {
        this.setName();
        this.setState({activeTab: "config"})
    }

    selectTemplate(templateId: number | string, config: ITemplateConfig) {
    	if (config) {
    		const templateConfig = {...config};
    		templateConfig.description = `Copy of ${config.description}`;
			this.setState({
				templateId,
				templateConfig,
			});
		} else {
    		const templateConfig = {...this.state.templateConfig};
    		let defaultTemplateConfig = templateHelper.getTemplateDefaultConfiguration(templateId);
    		const description = templateConfig.description;
    		if (description !== "Example dashboard") {
    			defaultTemplateConfig.description = templateConfig.description;
			}
			this.setState({
				templateId,
				templateConfig: defaultTemplateConfig,
			});
		}

    }

    toggleRenameField() {
    	this.setName();
        this.setState({renameActive: !this.state.renameActive});
    }

    changeName(e: any) {
        let templateConfig = {...this.state.templateConfig};
        templateConfig.description = e.target.value;
        this.setState({templateConfig});
    }

    updateConfig(itemId: number, itemConfig: IGridConfigItem) {
		let templateConfig = {...this.state.templateConfig};
		let gridConfig = [...templateConfig.grid_config];
		gridConfig[Number(itemId)] = itemConfig;
		templateConfig.grid_config = gridConfig;
		this.setState({
			templateConfig,
		});
    }
    
    toggleModal() {
        this.setState({
            modalOpen: !this.state.modalOpen
        })
    }

    uploadLogo(e: any) {
    	const file = e.target.files[0];
    	const isValid = this.validateLogo(file);
    	if (isValid) {
			const logo = {...this.state.logo};
			let fileReader = new FileReader();
			fileReader.onloadend = (e) => {
				// @ts-ignore
                logo.url = e.target.result;
				logo.name = file.name;
                // @ts-ignore
				this.setState({logo});
			};
			fileReader.readAsDataURL(file);
		} else {
    		e.target.value = null;
    		this.props.dispatch(showNotification('error', 'Invalid file extension.'));
		}
    }
    
    validateLogo(file: {name: string}) {
    	const validExtensions = ['jpg', 'png', 'jpeg', 'svg'];
    	const fileName =  file.name;
		const fileExt = fileName.substr(fileName.lastIndexOf('.') + 1);
		return validExtensions.indexOf(fileExt) !== -1;
	}
	
	saveDashboard() {
    	const config = {...this.state.templateConfig};
    	const logo = {...this.state.logo};
    	const data = {
			description: config.description,
			message : config.message,
			brands: config.brands,
			grid_type_id: config.grid_type_id,
			grid_type: config.grid_type,
			grid_config: config.grid_config,
			image: logo.url
		};
    	this.props.dispatch(saveDashboard(data));
	}

    render() {
        const description = this.state.templateConfig.description;
        return (
            <div className="container create-dashboard">
                <div className="row">
                    <div className="col-md-12">
                        <div className="create-dashboard__header">
                            {
                                this.state.renameActive ? (
                                    <Fragment>
                                        <input type="text" className="form-control" value={description} onChange={this.changeName}/>
                                        <button className="btn btn-outline-primary" onClick={this.toggleRenameField}>
                                            Save
                                        </button>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <h2 title={description}>{description}</h2>
                                        <button className="btn btn-outline-primary" onClick={this.toggleRenameField}>
                                            Rename
                                        </button>
                                    </Fragment>
                                )
                            }
                            <div className="ml-auto">
                                <button className="btn btn-outline-primary mr-2" onClick={this.toggleModal}>
                                    Upload logo
                                </button>
                                <button className="btn btn-primary" onClick={this.saveDashboard}>
                                    Save dashboard
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-12">
                        <div className="create-dashboard__flow">
                            <div className="steps">
                                <div
                                    className={`step-one ${this.state.activeTab === "template" ? "active" : ""}`}
                                    onClick={() => this.switchTab("template")}
                                >
                                    <span>1.Pick template</span>
                                    <div className="arrow-right"></div>
                                </div>
                                <div
                                    className={`step-two ${this.state.activeTab === "config" ? "active" : ""}`}
                                    onClick={() => this.switchTab("config")}
                                >
                                    <span>2.Configure components</span>
                                    <div className="arrow-right"></div>
                                </div>
                            </div>
                            {
                                this.state.activeTab === "template" ? (
                                    <button className="btn btn-primary next-step" onClick={() => this.switchTab("config")}>Next step</button>
                                ) : (
                                    <button className="btn btn-primary next-step" onClick={() => this.switchTab("template")}>Back</button>
                                )
                            }

                        </div>
                    </div>
                    
                    <div className="col-md-12">
                        <ErrorBoundary>
                            <TemplateTab
                                selectTemplate={this.selectTemplate}
                                isActive={this.state.activeTab === "template"}
                                templateId={this.state.templateId}
                                switchTab={this.switchTabViaTemplateDoubleClick}
                            />
                        </ErrorBoundary>
                    </div>
                   
                    <div className="col-md-12">
                        <ErrorBoundary>
                            <ConfigTab
                                isActive={this.state.activeTab === "config"}
                                updateConfig={this.updateConfig}
                                templateConfig={this.state.templateConfig}
                            />
                        </ErrorBoundary>
                    </div>
                </div>
                <UploadLogoModal 
                    isOpen={this.state.modalOpen}
                    toggleModal={this.toggleModal}
                    logo={this.state.logo}
					uploadLogo={this.uploadLogo}
                />
            </div>
        );
    }
}

export default connect()(CreateDashboard);
